/**
 * PrivacyPolicy Edit Form Component
 * Reference implementation following UI Standards
 */

import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, TextField, Button, Alert, CircularProgress,
  Breadcrumbs, Link as MuiLink, Stack, FormControlLabel, Checkbox
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { PrivacyPolicyUpdate } from '../../types/privacyPolicy';

const PrivacyPolicyEditForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState<PrivacyPolicyUpdate>({
    title: '',
    content: '',
    effective_date: '',
    is_active: true,
  });

  const [validationErrors, setValidationErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    const fetchPrivacyPolicy = async () => {
      if (!id) {
        setError('No privacy policy ID provided');
        setFetchingData(false);
        return;
      }

      try {
        const response = await axios.get(`/api/privacy-policies/${id}`);
        const policy = response.data;

        setFormData({
          title: policy.title || '',
          content: policy.content || '',
          effective_date: policy.effective_date || '',
          is_active: policy.is_active ?? true,
        });
      } catch (err: any) {
        console.error('Error fetching privacy policy:', err);
        if (err.response?.status === 404) {
          setError('Privacy policy not found');
        } else {
          setError('Failed to load privacy policy data. Please try again.');
        }
      } finally {
        setFetchingData(false);
      }
    };

    fetchPrivacyPolicy();
  }, [id]);

  const validate = (): boolean => {
    const errors: { [key: string]: string } = {};
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    if (!validate()) {
      setError('Please fix the validation errors before submitting');
      return;
    }

    setLoading(true);

    try {
      const payload: PrivacyPolicyUpdate = {};

      if (formData.title && formData.title.trim() !== '') payload.title = formData.title;
      if (formData.content && formData.content.trim() !== '') payload.content = formData.content;
      if (formData.effective_date && formData.effective_date.trim() !== '') payload.effective_date = formData.effective_date;
      if (formData.is_active !== undefined) payload.is_active = formData.is_active;

      await axios.patch(`/api/privacy-policies/${id}`, payload);
      setSuccess(true);

      setTimeout(() => {
        navigate('/privacy-policies');
      }, 1500);
    } catch (err: any) {
      console.error('Error updating privacy policy:', err);

      if (err.response?.data?.detail) {
        if (typeof err.response.data.detail === 'string') {
          setError(err.response.data.detail);
        } else if (Array.isArray(err.response.data.detail)) {
          const errorMessages = err.response.data.detail
            .map((e: any) => `${e.loc.join('.')}: ${e.msg}`)
            .join(', ');
          setError(errorMessages);
        } else {
          setError('Failed to update privacy policy. Please check your input.');
        }
      } else {
        setError('Failed to update privacy policy. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (fetchingData) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error && !formData.title) {
    return (
      <Box>
        <Alert severity="error">{error}</Alert>
        <Button onClick={() => navigate('/privacy-policies')} sx={{ mt: 2 }}>
          Back to Privacy Policies
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3 }}>
        <MuiLink component="button" variant="body1" onClick={() => navigate('/privacy-policies')} sx={{ cursor: 'pointer', textDecoration: 'none' }}>
          Privacy Policies
        </MuiLink>
        <Typography color="text.primary">Edit Policy</Typography>
      </Breadcrumbs>

      <Box mb={3}>
        <Typography variant="h4" gutterBottom>Edit Privacy Policy</Typography>
        <Typography variant="body2" color="textSecondary">Update the privacy policy details</Typography>
      </Box>

      {success && <Alert severity="success" sx={{ mb: 3 }}>Privacy policy updated successfully! Redirecting...</Alert>}
      {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

      <Paper sx={{ p: 4 }}>
        <form onSubmit={handleSubmit}>
          <Stack spacing={3}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                fullWidth label="Title" name="title"
                value={formData.title || ''} onChange={handleChange}
                error={!!validationErrors.title} helperText={validationErrors.title}
                disabled={loading}
              />
              <TextField
                fullWidth type="date" label="Effective Date" name="effective_date"
                value={formData.effective_date || ''} onChange={handleChange}
                error={!!validationErrors.effective_date} helperText={validationErrors.effective_date}
                disabled={loading} InputLabelProps={{ shrink: true }}
              />
            </Stack>

            <TextField
              fullWidth label="Content" name="content"
              value={formData.content || ''} onChange={handleChange}
              error={!!validationErrors.content} helperText={validationErrors.content}
              disabled={loading} multiline rows={10}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.is_active ?? true}
                  onChange={handleChange}
                  name="is_active"
                  disabled={loading}
                />
              }
              label="Active"
            />

            <Box display="flex" gap={2} justifyContent="flex-end" mt={2}>
              <Button variant="outlined" onClick={() => navigate('/privacy-policies')} disabled={loading}>
                Cancel
              </Button>
              <Button
                type="submit" variant="contained" color="primary" disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : null}
              >
                {loading ? 'Updating...' : 'Update Policy'}
              </Button>
            </Box>
          </Stack>
        </form>
      </Paper>
    </Box>
  );
};

export default PrivacyPolicyEditForm;
